import React from 'react';
import {
  Box,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  useColorModeValue,
  Icon,
  SimpleGrid,
} from '@chakra-ui/react';
import { FaPaperclip } from 'react-icons/fa';
import { Trans } from '@lingui/macro';

interface SalesPeriodData {
  vehicleCount: number;
  amount: string;
}

interface SalesSummaryData {
  today: SalesPeriodData;
  thisWeek: SalesPeriodData;
  currentMonth: SalesPeriodData;
}

interface SalesCardProps {
  salesSummary: SalesSummaryData;
}

// Helper component to display sales data
interface SalesDisplayProps {
  periodData: SalesPeriodData;
  labelColor: string;
  valueColor: string;
}

const SalesDisplay: React.FC<SalesDisplayProps> = ({ periodData, labelColor, valueColor }) => {
  return (
    <SimpleGrid columns={2} spacing={8} py={6}>
      <Box textAlign="center" borderColor="gray.200" pr={4}>
        <Stat alignSelf="center">
          <StatLabel color={labelColor} fontSize="sm" mb={2} letterSpacing="wide">
            <Trans>Toplam Ciro</Trans>
          </StatLabel>
          <Flex align="center" justify="center">
            <StatNumber fontSize="l" fontWeight="bold" color={valueColor}>
              {periodData.amount}
            </StatNumber>
          </Flex>
        </Stat>
      </Box>
      <Box textAlign="center" pl={4}>
        <Stat>
          <StatLabel color={labelColor} fontSize="sm" mb={2} letterSpacing="wide">
            <Trans>Satış Adedi</Trans>
          </StatLabel>
          <Flex align="center" justify="center">
            <StatNumber fontSize="l" fontWeight="bold" color={valueColor}>
              {periodData.vehicleCount}
            </StatNumber>
          </Flex>
        </Stat>
      </Box>
    </SimpleGrid>
  );
};

export const SalesCard: React.FC<SalesCardProps> = ({ salesSummary }) => {
  const headingColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const tabBg = useColorModeValue('gray.100', 'gray.700');
  const tabSelectedBg = useColorModeValue('white', 'gray.800');
  const tabColor = useColorModeValue('gray.600', 'gray.400');
  const tabSelectedColor = useColorModeValue('blue.600', 'blue.300');
  const statLabelColor = useColorModeValue('gray.500', 'gray.400');
  const statNumberColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box height="100%" width="100%" display="flex" flexDirection="column" minHeight="300px">
      <Flex mb={4} align="center">
        <Icon as={FaPaperclip} color={accentColor} mr={2} boxSize={5} />
        <Heading size="md" color={headingColor}>
          <Trans>Ozet Rapor</Trans>
        </Heading>
      </Flex>

      <Tabs variant="soft-rounded" colorScheme="blue" flex="1" display="flex" flexDirection="column">
        <TabList bg={tabBg} borderRadius="lg" p={1} mb={4}>
          <Tab
            flex="1"
            borderRadius="md"
            _selected={{ color: tabSelectedColor, bg: tabSelectedBg, fontWeight: 'bold' }}
            color={tabColor}
            fontWeight="medium"
          >
            <Trans>Bugün</Trans>
          </Tab>
          <Tab
            flex="1"
            borderRadius="md"
            _selected={{ color: tabSelectedColor, bg: tabSelectedBg, fontWeight: 'bold' }}
            color={tabColor}
            fontWeight="medium"
          >
            <Trans>Bu Hafta</Trans>
          </Tab>
          <Tab
            flex="1"
            borderRadius="md"
            _selected={{ color: tabSelectedColor, bg: tabSelectedBg, fontWeight: 'bold' }}
            color={tabColor}
            fontWeight="medium"
          >
            <Trans>Bu Ay</Trans>
          </Tab>
        </TabList>

        <TabPanels
          flex="1"
          bg={useColorModeValue('white', 'gray.800')}
          borderRadius="lg"
          borderWidth="1px"
          borderColor={borderColor}
          p={4}
        >
          <TabPanel>
            <SalesDisplay periodData={salesSummary.today} labelColor={statLabelColor} valueColor={statNumberColor} />
          </TabPanel>
          <TabPanel>
            <SalesDisplay periodData={salesSummary.thisWeek} labelColor={statLabelColor} valueColor={statNumberColor} />
          </TabPanel>
          <TabPanel>
            <SalesDisplay
              periodData={salesSummary.currentMonth}
              labelColor={statLabelColor}
              valueColor={statNumberColor}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};
